import React, { useState, useCallback, useEffect } from "react";
import {
  FaExclamationCircle, FaCheckCircle, FaInfoCircle,
  FaSearch, FaFilter, FaTimes, FaChevronLeft, FaChevronRight,
  FaLightbulb, FaUser, FaDesktop, FaNetworkWired, FaClock,
  FaLayerGroup, FaTag, FaRedo, FaDownload, FaTerminal,
  FaExclamationTriangle, FaShieldAlt, FaDatabase, FaCode,
  FaAngleRight, FaFire, FaHistory, FaClipboardList, FaSave, FaTrash, FaPen, FaPlus
} from "react-icons/fa";
import { monitoringApi } from "../../api/monitoring.api";
import "./ErrorLogsPanel.css";

// Constants.
const STATUS_FILTERS = ["Todos", "ERROR", "SUCCESS", "WARNING"];
const HOUR_WINDOWS   = [
  { label: "1h",  value: 1   },
  { label: "6h",  value: 6   },
  { label: "24h", value: 24  },
  { label: "48h", value: 48  },
  { label: "7d",  value: 168 },
];
const PAGE_SIZES = [20, 50, 100];

const KNOWN_MODULES = [
  "Todos",
  "ms-api-gateway",
  "ms-identity-access",
  "ms-emisiones",
  "ms-validations",
  "ms-interoperability",
  "ms-config-system",
  "ms-build",
  "ms-datamart",
  "ms-ai",
  "ms-notifications",
  "ms-monitoring",
];

// Helpers
function getCatIcon(cat, size = "1rem") {
  const style = { fontSize: size };
  if (cat === "ERROR")   return <FaExclamationCircle className="elp-cat-icon error"   style={style} />;
  if (cat === "SUCCESS") return <FaCheckCircle        className="elp-cat-icon success" style={style} />;
  return                        <FaInfoCircle         className="elp-cat-icon warning" style={style} />;
}

function formatDateTime(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("es-MX", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function formatRelative(dt) {
  if (!dt) return "";
  const s = Math.floor((Date.now() - new Date(dt)) / 1000);
  if (s < 60)  return `hace ${s}s`;
  if (s < 3600) return `hace ${Math.floor(s / 60)}m`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)}h`;
  return `hace ${Math.floor(s / 86400)}d`;
}

function exportCsv(logs) {
  const headers = ["ID","Fecha","Módulo","Acción","Usuario","Rol","IP","Status","Categoría","Mensaje"];
  const rows = logs.map(l => [
    l.id,
    formatDateTime(l.createdAt),
    l.module || "",
    l.action || "",
    l.username || "",
    l.role || "",
    l.ipAddress || "",
    l.status || "",
    l.category || "",
    `"${(l.humanReadableMessage || "").replace(/"/g,'""')}"`,
  ].join(","));
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `noc-logs-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Detailed diagnostics by error type.
function getDiagnosticInfo(log) {
  const action = (log.action || "").toUpperCase();
  const module = log.module || "";
  const cat    = log.category;

  const base = {
    errorCode:   log.status || (cat === "ERROR" ? "ERR_UNHANDLED" : cat === "WARNING" ? "WARN_001" : "OK"),
    component:   module || "Sistema",
    affectedArea: "Sin determinar",
    steps: [],
    commands: [],
    references: [],
  };

  if (cat === "ERROR") {
    if (action.includes("LOGIN") || action.includes("AUTH")) {
      return {
        ...base,
        errorCode: "AUTH_FAILED",
        affectedArea: "Módulo de Autenticación / Identity Access",
        summary: "Fallo en el proceso de autenticación del usuario.",
        rootCause: `La cuenta "${log.username || "desconocida"}" intentó acceder y fue rechazada. Posibles causas: credenciales incorrectas, cuenta bloqueada por intentos fallidos, token expirado o sesión inválida.`,
        steps: [
          "Verifica si la cuenta existe en la base de datos de usuarios.",
          "Comprueba si el usuario está bloqueado por intentos fallidos.",
          "Revisa la configuración del proveedor de identidad (Keycloak/LDAP).",
          "Verifica que el token JWT no haya expirado (TTL configurado).",
        ],
        commands: [
          `docker compose logs ms-identity-access --tail=100 | grep "${log.username || "error"}"`,
          `SELECT * FROM users WHERE username='${log.username || "?"}' LIMIT 1;`,
          `curl -X POST http://localhost:8080/auth/login -d '{"username":"${log.username || "?"}","password":"***"}'`,
        ],
        references: [
          "Documentación: Gestión de accesos - ms-identity-access",
          "Procedimiento: Desbloqueo de cuenta de usuario",
        ],
      };
    }

    if (action.includes("CERTIFICATE") || action.includes("EMISION")) {
      return {
        ...base,
        errorCode: "CERT_EMISSION_FAILED",
        affectedArea: "ms-emisiones / Certificación Digital",
        summary: "Error durante la emisión o firma de un certificado.",
        rootCause: "El proceso de emisión no pudo completarse. Causas frecuentes: schema XSD inválido, datos del solicitante incompletos, fallo en el servicio de firma digital, o error en la conexión con la CA raíz.",
        steps: [
          "Revisa si el schema XSD tiene la versión correcta activa.",
          "Verifica los datos del solicitante: CURP, RFC y nombre completo.",
          "Comprueba la conectividad con el servicio de firma (PKI).",
          "Revisa si hay cola de emisión pendiente sin procesar.",
        ],
        commands: [
          "docker compose logs ms-emisiones --tail=50",
          "SELECT * FROM audit_log WHERE action LIKE '%CERTIFICATE%' ORDER BY created_at DESC LIMIT 10;",
          "curl -X GET http://localhost:8082/api/certificates/pending",
        ],
        references: [
          "Documentación: Proceso de emisión de certificados",
          "Procedimiento: Validación del schema XSD activo",
        ],
      };
    }

    if (action.includes("VALIDATION")) {
      return {
        ...base,
        errorCode: "VALIDATION_INTEGRITY_ERROR",
        affectedArea: "ms-validations / Validación de documentos",
        summary: "La validación de integridad del documento falló.",
        rootCause: "La firma digital del documento no coincide con el contenido actual. El documento pudo haber sido modificado después de ser firmado, o hay un error en el proceso de verificación criptográfica.",
        steps: [
          "Verifica la cadena de certificación completa (Root CA → Intermedia → Leaf).",
          "Comprueba que el algoritmo de hash sea SHA-256 o superior.",
          "Revisa si el certificado del firmante está revocado (CRL/OCSP).",
          "Valida el sellado de tiempo (timestamp) del documento.",
        ],
        commands: [
          "openssl verify -CAfile ca-chain.crt document-cert.pem",
          "docker compose logs ms-validations --tail=50",
          "curl -X POST http://localhost:8083/api/validate -d @document.xml",
        ],
        references: [
          "Procedimiento: Verificación de firma digital",
          "RFC 5280: Internet X.509 PKI Certificate and CRL Profile",
        ],
      };
    }

    if (action.includes("INTEROP") || module.includes("interop")) {
      return {
        ...base,
        errorCode: "INTEROP_CONNECTIVITY_ERROR",
        affectedArea: "ms-interoperability / Integración externa",
        summary: "Fallo en la comunicación con un servicio externo.",
        rootCause: "El microservicio de interoperabilidad no pudo establecer conexión con el servicio de destino. Causas posibles: timeout de red, credenciales de API inválidas, endpoint externo caído o cambio en el esquema de la API externa.",
        steps: [
          "Verifica la conectividad de red hacia el host externo (ping/telnet).",
          "Comprueba que las credenciales de API externas sean válidas y no hayan expirado.",
          "Revisa el endpoint configurado en application.yml del módulo.",
          "Consulta el SLA y disponibilidad del servicio externo.",
        ],
        commands: [
          `docker compose logs ms-interoperability --tail=50`,
          `curl -v https://servicio-externo/api/health`,
          `kubectl get pods -n certificacion | grep interop`,
        ],
        references: [
          "Documentación: Configuración de endpoints externos",
          "Procedimiento: Rotación de credenciales de API externa",
        ],
      };
    }

    // Generic error.
    return {
      ...base,
      errorCode: log.status || "ERR_GENERIC",
      affectedArea: module || "Sin determinar",
      summary: "Error no clasificado en el sistema.",
      rootCause: "Ocurrió un error no esperado. Revisa los logs del contenedor correspondiente para más detalles.",
      steps: [
        "Consulta los logs del contenedor afectado.",
        "Verifica el estado de salud de todos los microservicios.",
        "Revisa los recursos del servidor (CPU, memoria, disco).",
        "Contacta al equipo de soporte si el error persiste.",
      ],
      commands: [
        `docker compose logs ${module || "ms-monitoring"} --tail=100`,
        `docker stats --no-stream`,
        `curl http://localhost:8080/actuator/health`,
      ],
      references: [
        "Runbook: Procedimiento general de diagnóstico",
      ],
    };
  }

  if (cat === "WARNING") {
    return {
      ...base,
      errorCode: "WARN_THRESHOLD",
      affectedArea: module || "Sistema",
      summary: "Se detectó una situación anormal pero no crítica.",
      rootCause: "El sistema detectó un comportamiento fuera del umbral normal. Si se repite con frecuencia, puede escalar a un error crítico.",
      steps: [
        "Monitorea la frecuencia de esta advertencia.",
        "Verifica si hay tendencia creciente en el mismo módulo.",
        "Revisa la configuración de umbrales en AlertRules.",
      ],
      commands: [
        `docker compose logs ${module || "ms-monitoring"} --since=1h`,
      ],
      references: [
        "Documentación: Configuración de reglas de alerta",
      ],
    };
  }

  return {
    ...base,
    errorCode: "OK",
    affectedArea: module || "Sistema",
    summary: "Operación completada exitosamente.",
    rootCause: "Este es un registro informativo. No se requiere acción.",
    steps: ["No se requiere intervención del técnico."],
    commands: [],
    references: [],
  };
}

// Main component.
export default function ErrorLogsPanel() {
  const [statusFilter, setStatusFilter]     = useState("Todos");
  const [moduleFilter, setModuleFilter]     = useState("Todos");
  const [usernameFilter, setUsernameFilter] = useState("");
  const [hoursFilter, setHoursFilter]       = useState(24);
  const [errorsOnly, setErrorsOnly]         = useState(false);

  const [page, setPage]         = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [logs, setLogs]                     = useState([]);
  const [totalElements, setTotalElements]   = useState(0);
  const [totalPages, setTotalPages]         = useState(0);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [stats, setStats]                   = useState({ errors: 0, warnings: 0, success: 0 });

  const [selectedLog, setSelectedLog]       = useState(null);
  const [detailTab, setDetailTab]           = useState("resumen");

  // Fetch
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since = new Date(Date.now() - hoursFilter * 3600000).toISOString().slice(0, 19);
      let result;
      if (errorsOnly) {
        result = await monitoringApi.getRecentErrors({
          module: moduleFilter !== "Todos" ? moduleFilter : undefined,
          hours:  hoursFilter,
          page,
          size:   pageSize,
        });
      } else {
        result = await monitoringApi.getLogs({
          module:   moduleFilter !== "Todos" ? moduleFilter : undefined,
          status:   statusFilter !== "Todos" ? statusFilter : undefined,
          username: usernameFilter || undefined,
          since,
          page,
          size:     pageSize,
        });
      }

      const content = Array.isArray(result) ? result : (result?.content ?? []);
      setLogs(content);
      setTotalElements(result?.totalElements ?? content.length);
      setTotalPages(result?.totalPages ?? Math.ceil((result?.totalElements ?? content.length) / pageSize));

      // Compute stats
      const errCount  = content.filter(l => l.category === "ERROR").length;
      const warnCount = content.filter(l => l.category === "WARNING").length;
      const okCount   = content.filter(l => l.category === "SUCCESS").length;
      setStats({ errors: errCount, warnings: warnCount, success: okCount });
    } catch (err) {
      console.error("Error fetching logs:", err);
      setError("No se pudo cargar el historial. Verifica la conexión con ms-monitoring.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, moduleFilter, usernameFilter, hoursFilter, errorsOnly, page, pageSize]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const resetFilters = () => {
    setStatusFilter("Todos");
    setModuleFilter("Todos");
    setUsernameFilter("");
    setHoursFilter(24);
    setErrorsOnly(false);
    setPage(0);
  };

  // Render
  return (
    <div className="elp-wrapper">

      {/* ── Header ── */}
      <div className="elp-header">
        <div className="elp-header-left">
          <div className="elp-header-icon-box">
            <FaHistory className="elp-header-icon" />
          </div>
          <div>
            <h3 className="elp-title">Historial de Logs del Sistema</h3>
            <p className="elp-subtitle">
              {totalElements.toLocaleString()} registros encontrados
              {errorsOnly && <span className="elp-errors-badge"> · solo errores</span>}
            </p>
          </div>
        </div>

        {/* Quick stats. */}
        <div className="elp-quick-stats">
          <div className="elp-qs-chip error">
            <FaFire />
            <span>{stats.errors}</span>
            <small>errores</small>
          </div>
          <div className="elp-qs-chip warning">
            <FaExclamationTriangle />
            <span>{stats.warnings}</span>
            <small>advertencias</small>
          </div>
          <div className="elp-qs-chip success">
            <FaShieldAlt />
            <span>{stats.success}</span>
            <small>exitosos</small>
          </div>
        </div>

        <div className="elp-header-actions">
          <button
            className={`elp-toggle-btn ${errorsOnly ? "active" : ""}`}
            onClick={() => { setErrorsOnly(!errorsOnly); setPage(0); }}
            title="Mostrar solo errores"
          >
            <FaExclamationCircle /> Solo errores
          </button>
          <button className="elp-action-btn" onClick={fetchLogs} title="Actualizar">
            <FaRedo className={loading ? "elp-spinning" : ""} />
          </button>
          {logs.length > 0 && (
            <button className="elp-action-btn" onClick={() => exportCsv(logs)} title="Exportar CSV">
              <FaDownload />
            </button>
          )}
        </div>
      </div>

      {/* Filters. */}
      <div className="elp-filters">
        <div className="elp-filter-group">
          <label className="elp-filter-label"><FaClock /> Período</label>
          <div className="elp-btn-group">
            {HOUR_WINDOWS.map(w => (
              <button
                key={w.value}
                className={`elp-time-btn ${hoursFilter === w.value ? "active" : ""}`}
                onClick={() => { setHoursFilter(w.value); setPage(0); }}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        <div className="elp-filter-group">
          <label className="elp-filter-label"><FaLayerGroup /> Módulo</label>
          <select
            className="elp-select"
            value={moduleFilter}
            onChange={e => { setModuleFilter(e.target.value); setPage(0); }}
          >
            {KNOWN_MODULES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {!errorsOnly && (
          <div className="elp-filter-group">
            <label className="elp-filter-label"><FaTag /> Status</label>
            <div className="elp-btn-group">
              {STATUS_FILTERS.map(s => (
                <button
                  key={s}
                  className={`elp-status-btn ${s.toLowerCase()} ${statusFilter === s ? "active" : ""}`}
                  onClick={() => { setStatusFilter(s); setPage(0); }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="elp-filter-group">
          <label className="elp-filter-label"><FaSearch /> Usuario</label>
          <div className="elp-search-box">
            <input
              type="text"
              className="elp-input"
              placeholder="Buscar usuario..."
              value={usernameFilter}
              onChange={e => setUsernameFilter(e.target.value)}
              onKeyDown={e => e.key === "Enter" && fetchLogs()}
            />
            {usernameFilter && (
              <button className="elp-clear-btn" onClick={() => { setUsernameFilter(""); setPage(0); }}>
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <button className="elp-reset-btn" onClick={resetFilters} title="Limpiar filtros">
          <FaFilter /> Limpiar
        </button>
      </div>

      {/* Table. */}
      <div className="elp-table-container">
        {loading && (
          <div className="elp-loading">
            <div className="elp-spinner" />
            <span>Cargando logs...</span>
          </div>
        )}

        {!loading && error && (
          <div className="elp-error-msg">
            <FaExclamationCircle /> {error}
          </div>
        )}

        {!loading && !error && logs.length === 0 && (
          <div className="elp-empty">
            <FaCheckCircle className="elp-empty-icon" />
            <p>Sin registros con los filtros actuales.</p>
            <small>Prueba ampliando el período o cambiando los filtros.</small>
          </div>
        )}

        {!loading && !error && logs.length > 0 && (
          <table className="elp-table">
            <thead>
              <tr>
                <th className="elp-th-sm">Estado</th>
                <th>Fecha / Hora</th>
                <th>Módulo</th>
                <th>Acción</th>
                <th>Usuario</th>
                <th>IP</th>
                <th>Descripción</th>
                <th className="elp-th-sm">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => (
                <tr
                  key={log.id ?? idx}
                  className={`elp-row ${log.category?.toLowerCase()} ${selectedLog?.id === log.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedLog(selectedLog?.id === log.id ? null : log);
                    setDetailTab("resumen");
                  }}
                >
                  <td className="elp-td-cat">
                    {getCatIcon(log.category)}
                    <span className={`elp-status-pill ${log.category?.toLowerCase()}`}>
                      {log.status || log.category}
                    </span>
                  </td>
                  <td className="elp-td-mono">
                    <div>{formatDateTime(log.createdAt)}</div>
                    <div className="elp-td-relative">{formatRelative(log.createdAt)}</div>
                  </td>
                  <td>
                    <span className="elp-module-badge">{log.module || "—"}</span>
                  </td>
                  <td className="elp-td-action">{log.action || "—"}</td>
                  <td className="elp-td-mono">{log.username || "sistema"}</td>
                  <td className="elp-td-mono elp-td-ip">{log.ipAddress || "—"}</td>
                  <td className="elp-td-msg">
                    <span title={log.humanReadableMessage}>{log.humanReadableMessage}</span>
                  </td>
                  <td className="elp-td-action-icon">
                    <FaAngleRight className={`elp-row-icon ${selectedLog?.id === log.id ? "rotated" : ""}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Advanced detail panel. */}
      {selectedLog && <DetailPanel log={selectedLog} onClose={() => setSelectedLog(null)} tab={detailTab} setTab={setDetailTab} />}

      {/* Pagination. */}
      {totalPages > 1 && (
        <div className="elp-pagination">
          <div className="elp-page-info">
            Página {page + 1} de {totalPages} · {totalElements.toLocaleString()} registros
          </div>
          <div className="elp-page-controls">
            <select
              className="elp-select elp-select-sm"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
            >
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / pág</option>)}
            </select>
            <button className="elp-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <FaChevronLeft />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let p = i;
              if (totalPages > 7) {
                if      (page < 4)              p = i;
                else if (page > totalPages - 4) p = totalPages - 7 + i;
                else                            p = page - 3 + i;
              }
              return (
                <button key={p} className={`elp-page-num ${page === p ? "active" : ""}`} onClick={() => setPage(p)}>
                  {p + 1}
                </button>
              );
            })}
            <button className="elp-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Advanced detail panel.
function DetailPanel({ log, onClose, tab, setTab }) {
  const diag = getDiagnosticInfo(log);

  const TABS = [
    { id: "resumen",     label: "Resumen",      icon: <FaInfoCircle /> },
    { id: "diagnostico", label: "Diagnóstico",  icon: <FaExclamationTriangle /> },
    { id: "comandos",    label: "Comandos",      icon: <FaTerminal /> },
    { id: "metadata",    label: "Metadatos",     icon: <FaDatabase /> },
    { id: "incidente",   label: "Incidente",     icon: <FaClipboardList /> },
  ];

  return (
    <div className="elp-detail-panel">
      {/* Header */}
      <div className="elp-detail-header">
        <div className="elp-detail-title-row">
          <div className={`elp-detail-severity-dot ${log.category?.toLowerCase()}`} />
          {getCatIcon(log.category, "1rem")}
          <span className="elp-detail-title-text">
            Evento #{log.id} — {log.action || log.module || "Sistema"}
          </span>
          <span className={`elp-error-code-badge ${log.category?.toLowerCase()}`}>
            {diag.errorCode}
          </span>
        </div>
        <button className="elp-close-btn" onClick={onClose}><FaTimes /></button>
      </div>

      {/* Tabs */}
      <div className="elp-detail-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`elp-tab-btn ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content. */}
      <div className="elp-detail-content">

        {/* TAB: Summary. */}
        {tab === "resumen" && (
          <div className="elp-tab-pane">
            <div className={`elp-detail-message ${log.category?.toLowerCase()}`}>
              <p className="elp-detail-msg-text">{log.humanReadableMessage}</p>
            </div>

            <div className="elp-summary-card">
              <h4 className="elp-summary-heading">📋 Resumen del evento</h4>
              <p className="elp-summary-text">{diag.summary}</p>
            </div>

            {log.suggestedAction && (
              <div className="elp-suggestion">
                <FaLightbulb className="elp-suggestion-icon" />
                <div>
                  <p className="elp-suggestion-title">Acción sugerida</p>
                  <p className="elp-suggestion-text">{log.suggestedAction}</p>
                </div>
              </div>
            )}

            <div className="elp-info-row">
              <InfoBadge icon={<FaClock />}       label="Fecha"    value={formatDateTime(log.createdAt)} />
              <InfoBadge icon={<FaLayerGroup />}  label="Módulo"   value={log.module}   />
              <InfoBadge icon={<FaUser />}        label="Usuario"  value={log.username} />
              <InfoBadge icon={<FaNetworkWired />} label="IP"      value={log.ipAddress}/>
            </div>
          </div>
        )}

        {/* TAB: Diagnostics. */}
        {tab === "diagnostico" && (
          <div className="elp-tab-pane">
            <div className="elp-diag-section">
              <h4 className="elp-diag-heading">
                <FaExclamationCircle /> Causa raíz identificada
              </h4>
              <p className="elp-diag-text">{diag.rootCause}</p>
            </div>

            <div className="elp-diag-section">
              <h4 className="elp-diag-heading">
                <FaCode /> Área afectada
              </h4>
              <div className="elp-tag-row">
                <span className="elp-diag-tag">{diag.affectedArea}</span>
                <span className="elp-diag-tag muted">{diag.component}</span>
              </div>
            </div>

            <div className="elp-diag-section">
              <h4 className="elp-diag-heading">
                <FaShieldAlt /> Pasos de resolución
              </h4>
              <ol className="elp-steps-list">
                {diag.steps.map((step, i) => (
                  <li key={i} className="elp-step-item">
                    <span className="elp-step-num">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {diag.references.length > 0 && (
              <div className="elp-diag-section">
                <h4 className="elp-diag-heading">📚 Referencias</h4>
                <ul className="elp-ref-list">
                  {diag.references.map((r, i) => (
                    <li key={i} className="elp-ref-item">{r}</li>
                  ))}
                </ul>
              </div>
            )}

            <InterpretationGuide log={log} />
          </div>
        )}

        {/* TAB: Commands. */}
        {tab === "comandos" && (
          <div className="elp-tab-pane">
            <p className="elp-cmd-intro">
              Ejecuta los siguientes comandos en el servidor para diagnosticar y resolver el problema:
            </p>
            {diag.commands.length === 0 ? (
              <div className="elp-cmd-empty">
                <FaCheckCircle /> No se requieren comandos para este evento.
              </div>
            ) : (
              diag.commands.map((cmd, i) => (
                <CommandBlock key={i} cmd={cmd} index={i + 1} />
              ))
            )}
            <div className="elp-cmd-note">
              <FaExclamationTriangle />
              <span>Ejecuta estos comandos con precaución en entornos de producción. Documenta los resultados.</span>
            </div>
          </div>
        )}

        {/* TAB: Metadata. */}
        {tab === "metadata" && (
          <div className="elp-tab-pane">
            <div className="elp-meta-grid">
              <MetaRow label="ID Registro"    value={log.id}          />
              <MetaRow label="Fecha"          value={formatDateTime(log.createdAt)} />
              <MetaRow label="Módulo"         value={log.module}      />
              <MetaRow label="Acción"         value={log.action}      />
              <MetaRow label="Status"         value={log.status}      />
              <MetaRow label="Categoría"      value={log.category}    />
              <MetaRow label="Usuario"        value={log.username}    />
              <MetaRow label="Rol"            value={log.role}        />
              <MetaRow label="IP"             value={log.ipAddress}   mono />
              <MetaRow label="Navegador"      value={log.browser}     />
              <MetaRow label="Sistema Op."    value={log.os}          />
              <MetaRow label="Cód. error"     value={diag.errorCode}  mono />
              <MetaRow label="Área afectada"  value={diag.affectedArea} />
            </div>
          </div>
        )}

        {/* TAB: Incident. */}
        {tab === "incidente" && (
          <IncidentPanel log={log} />
        )}
      </div>
    </div>
  );
}

// Subcomponents.
function InfoBadge({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="elp-info-badge">
      <span className="elp-info-icon">{icon}</span>
      <div>
        <span className="elp-info-label">{label}</span>
        <span className="elp-info-value">{value}</span>
      </div>
    </div>
  );
}

function CommandBlock({ cmd, index }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="elp-cmd-block">
      <div className="elp-cmd-header">
        <span className="elp-cmd-label">Comando {index}</span>
        <button className="elp-cmd-copy" onClick={copy}>
          {copied ? "✓ Copiado" : "Copiar"}
        </button>
      </div>
      <pre className="elp-cmd-pre"><code>{cmd}</code></pre>
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="elp-meta-row">
      <span className="elp-meta-label">{label}</span>
      <span className={`elp-meta-value ${mono ? "mono" : ""}`}>{value}</span>
    </div>
  );
}

// NOC incidents panel.
const INCIDENT_STATUSES = [
  { value: "OPEN",          label: "Abierto",         color: "#dc3545" },
  { value: "INVESTIGATING", label: "Investigando",    color: "#fd7e14" },
  { value: "ATTENDED",      label: "Atendido",         color: "#0d6efd" },
  { value: "RESOLVED",      label: "Resuelto",         color: "#198754" },
];

function StatusBadgeInc({ status }) {
  const s = INCIDENT_STATUSES.find(x => x.value === status) ||
            { label: status, color: "#6c757d" };
  return (
    <span className="elp-inc-status-badge" style={{ background: s.color }}>
      {s.label}
    </span>
  );
}

function IncidentPanel({ log }) {
  const [notes, setNotes]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [showForm, setShowForm]     = useState(false);

  const [formNote, setFormNote]         = useState("");
  const [formCorrective, setFormCorrective] = useState("");
  const [formStatus, setFormStatus]     = useState("OPEN");

  const loadNotes = useCallback(async () => {
    if (!log?.id) return;
    setLoading(true);
    try {
      const data = await monitoringApi.getNotesForLog(log.id);
      setNotes(Array.isArray(data) ? data : []);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [log?.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const resetForm = () => {
    setFormNote("");
    setFormCorrective("");
    setFormStatus("OPEN");
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (note) => {
    setEditingId(note.id);
    setFormNote(note.note || "");
    setFormCorrective(note.correctiveAction || "");
    setFormStatus(note.status || "OPEN");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formNote.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await monitoringApi.updateIncidentNote(editingId, {
          note: formNote,
          correctiveAction: formCorrective || undefined,
          status: formStatus,
        });
      } else {
        const operator = sessionStorage.getItem("username") || "noc-operator";
        await monitoringApi.createIncidentNote({
          logId:             log.id,
          module:            log.module,
          action:            log.action,
          note:              formNote,
          correctiveAction:  formCorrective || undefined,
          status:            formStatus,
          operatorUsername:  operator,
        });
      }
      resetForm();
      await loadNotes();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta nota de incidente?")) return;
    try {
      await monitoringApi.deleteIncidentNote(id);
      await loadNotes();
    } catch { /* ignore */ }
  };

  return (
    <div className="elp-tab-pane">
      {/* Panel header. */}
      <div className="elp-inc-header">
        <h4 className="elp-inc-title">
          <FaClipboardList /> Bitácora de Incidente
        </h4>
        {!showForm && (
          <button className="elp-inc-new-btn" onClick={() => setShowForm(true)}>
            <FaPlus /> Nueva nota
          </button>
        )}
      </div>

      <p className="elp-inc-desc">
        Registra observaciones, acciones correctivas y el estado de atención
        para este evento. Las notas son visibles para todo el equipo NOC.
      </p>

      {/* Create/edit form. */}
      {showForm && (
        <div className="elp-inc-form">
          <div className="elp-inc-form-row">
            <label className="elp-inc-label">Estado</label>
            <div className="elp-inc-status-row">
              {INCIDENT_STATUSES.map(s => (
                <button
                  key={s.value}
                  className={`elp-inc-status-opt ${formStatus === s.value ? "active" : ""}`}
                  style={formStatus === s.value ? { borderColor: s.color, color: s.color } : {}}
                  onClick={() => setFormStatus(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="elp-inc-form-row">
            <label className="elp-inc-label">Observación <span className="elp-req">*</span></label>
            <textarea
              className="elp-inc-textarea"
              placeholder="Describe qué ocurrió, síntomas observados, impacto..."
              value={formNote}
              onChange={e => setFormNote(e.target.value)}
              rows={3}
            />
          </div>

          <div className="elp-inc-form-row">
            <label className="elp-inc-label">Acción correctiva (opcional)</label>
            <textarea
              className="elp-inc-textarea"
              placeholder="Pasos realizados para resolver el problema..."
              value={formCorrective}
              onChange={e => setFormCorrective(e.target.value)}
              rows={2}
            />
          </div>

          <div className="elp-inc-form-actions">
            <button
              className="elp-inc-cancel-btn"
              onClick={resetForm}
            >
              <FaTimes /> Cancelar
            </button>
            <button
              className="elp-inc-save-btn"
              onClick={handleSave}
              disabled={saving || !formNote.trim()}
            >
              <FaSave /> {saving ? "Guardando..." : editingId ? "Actualizar" : "Guardar nota"}
            </button>
          </div>
        </div>
      )}

      {/* Notes list. */}
      {loading && (
        <div className="elp-inc-loading">
          <div className="elp-spinner" /> Cargando notas...
        </div>
      )}

      {!loading && notes.length === 0 && !showForm && (
        <div className="elp-inc-empty">
          <FaClipboardList className="elp-inc-empty-icon" />
          <p>Sin notas de incidente registradas para este evento.</p>
          <small>Usa el botón "Nueva nota" para documentar observaciones.</small>
        </div>
      )}

      {!loading && notes.length > 0 && (
        <div className="elp-inc-list">
          {notes.map(n => (
            <div key={n.id} className="elp-inc-card">
              <div className="elp-inc-card-header">
                <StatusBadgeInc status={n.status} />
                <span className="elp-inc-operator">
                  <FaUser /> {n.operatorUsername}
                </span>
                <span className="elp-inc-date">
                  <FaClock /> {
                    new Date(n.createdAt).toLocaleString("es-MX", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit"
                    })
                  }
                </span>
                <div className="elp-inc-card-actions">
                  <button
                    className="elp-inc-icon-btn edit"
                    title="Editar nota"
                    onClick={() => handleEdit(n)}
                  >
                    <FaPen />
                  </button>
                  <button
                    className="elp-inc-icon-btn delete"
                    title="Eliminar nota"
                    onClick={() => handleDelete(n.id)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <p className="elp-inc-note-text">{n.note}</p>

              {n.correctiveAction && (
                <div className="elp-inc-corrective">
                  <span className="elp-inc-corrective-label">Acción correctiva:</span>
                  <p>{n.correctiveAction}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InterpretationGuide({ log }) {
  const cat    = log.category;
  const action = (log.action || "").toUpperCase();

  if (cat === "ERROR") {
    return (
      <div className="elp-guide error">
        <h4 className="elp-guide-title">🔍 Guía de interpretación</h4>
        <ul className="elp-guide-list">
          <li>Este evento registra un <strong>fallo en el sistema</strong> que requiere atención.</li>
          {action.includes("LOGIN") && <li>Verifica si la cuenta <code>{log.username}</code> está activa y no bloqueada.</li>}
          {action.includes("CERTIFICATE") && <li>Revisa la integridad del schema XSD y los datos del solicitante.</li>}
          {action.includes("VALIDATION") && <li>Verifica la cadena de firma digital completa.</li>}
          {action.includes("INTEROP") && <li>Revisa la conectividad con el servicio externo.</li>}
          <li>Consulta el contenedor: <code>docker compose logs {log.module} --tail=50</code></li>
        </ul>
      </div>
    );
  }
  if (cat === "WARNING") {
    return (
      <div className="elp-guide warning">
        <h4 className="elp-guide-title">⚠️ Guía de interpretación</h4>
        <ul className="elp-guide-list">
          <li>Situación <strong>anormal pero no crítica</strong>.</li>
          <li>Monitorea la frecuencia. Si se repite, escala a error.</li>
        </ul>
      </div>
    );
  }
  return (
    <div className="elp-guide success">
      <h4 className="elp-guide-title">✅ Guía de interpretación</h4>
      <ul className="elp-guide-list">
        <li>Operación completada exitosamente.</li>
        <li>No requiere intervención del técnico.</li>
      </ul>
    </div>
  );
}
